/**
 * FoundryDetailsTab — Accordion sections for Tools, Knowledge, Memory, Guardrails.
 *
 * Displays the Foundry agent's configuration details in a read-mostly view.
 * Tools and Knowledge come from the selected Foundry agent definition;
 * Memory and Guardrails are placeholders pointing users to the Foundry Portal.
 */

import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Badge,
  Button,
} from '@fluentui/react-components';
import {
  WrenchRegular,
  BookRegular,
  BrainCircuitRegular,
  ShieldCheckmarkRegular,
  OpenRegular,
  BotSparkleFilled,
} from '@fluentui/react-icons';
import type { FoundryAgent, FoundryAgentVersion } from '@/services/foundryAgentService';
import { useFoundryAgent, useResolvedFoundryEndpoint } from '@/hooks/useFoundryAgents';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  headerText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  noAgentMessage: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  sectionIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  toolList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  toolItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    transitionProperty: 'all',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  toolName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  toolType: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  emptyState: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    padding: tokens.spacingVerticalS,
  },
  editLink: {
    marginTop: tokens.spacingVerticalS,
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface FoundryDetailsTabProps {
  /** Selected Foundry agent (null if no agent selected) */
  agent?: FoundryAgent | null | undefined;
  /** Raw endpoint from the connection (may be legacy cognitiveservices URL) */
  rawEndpoint?: string | undefined;
  /** ARM resource ID of the connection's cognitive services account */
  connectionResourceId?: string | undefined;
  /** Agent ID — used to fetch agent when agent prop is not provided */
  agentId?: string | undefined;
  /** Agent API version (v1 assistants or v2 agents) */
  agentVersion?: FoundryAgentVersion | undefined;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const FoundryDetailsTab = ({ agent: agentProp, rawEndpoint, connectionResourceId, agentId, agentVersion }: FoundryDetailsTabProps) => {
  const styles = useStyles();

  // Resolve legacy endpoints
  const { resolvedEndpoint: projectEndpoint } = useResolvedFoundryEndpoint(rawEndpoint, connectionResourceId);

  // Fetch agent data if not provided directly
  const { data: fetchedAgent } = useFoundryAgent(
    projectEndpoint ?? '',
    agentId ?? '',
    !!projectEndpoint && !!agentId && !agentProp,
    agentVersion
  );
  const agent = agentProp ?? fetchedAgent ?? null;

  const toolsSummary = useMemo(() => {
    if (!agent?.tools || agent.tools.length === 0) return [];
    return agent.tools.map((tool) => ({
      type: tool.type,
      name: tool.type === 'function' ? tool.function?.name ?? 'Function' : tool.type,
      description: tool.type === 'function' ? tool.function?.description : undefined,
    }));
  }, [agent]);

  const foundryPortalUrl = 'https://ai.azure.com';

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <BotSparkleFilled />
        </div>
        <Text className={styles.headerText}>Agent Details (Foundry)</Text>
      </div>

      {/* No agent selected */}
      {!agent && (
        <Text className={styles.noAgentMessage}>
          Select an agent in the Parameters tab to view its configuration.
        </Text>
      )}

      <Accordion multiple collapsible defaultOpenItems={['tools']}>
        {/* Tools */}
        <AccordionItem value="tools">
          <AccordionHeader size="small">
            <div className={styles.accordionHeader}>
              <WrenchRegular className={styles.sectionIcon} />
              <Text className={styles.sectionLabel}>Tools</Text>
              {toolsSummary.length > 0 && (
                <Badge appearance="filled" size="small" color="informative">
                  {toolsSummary.length}
                </Badge>
              )}
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <div className={styles.toolList}>
              {toolsSummary.length > 0 ? (
                toolsSummary.map((tool, i) => (
                  <div key={i} className={styles.toolItem}>
                    <Text className={styles.toolName}>{tool.name}</Text>
                    <Text className={styles.toolType}>{tool.type}</Text>
                  </div>
                ))
              ) : (
                <Text className={styles.emptyState}>
                  {agent ? 'No tools configured' : 'Select an agent to see tools'}
                </Text>
              )}
              <Button
                className={styles.editLink}
                appearance="subtle"
                size="small"
                icon={<OpenRegular />}
                as="a"
                href={foundryPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Manage tools in Foundry Portal
              </Button>
            </div>
          </AccordionPanel>
        </AccordionItem>

        {/* Knowledge */}
        <AccordionItem value="knowledge">
          <AccordionHeader size="small">
            <div className={styles.accordionHeader}>
              <BookRegular className={styles.sectionIcon} />
              <Text className={styles.sectionLabel}>Knowledge</Text>
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <Text className={styles.emptyState}>
              Knowledge sources are configured in the Foundry Portal.
            </Text>
            <Button
              className={styles.editLink}
              appearance="subtle"
              size="small"
              icon={<OpenRegular />}
              as="a"
              href={foundryPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Manage knowledge in Foundry Portal
            </Button>
          </AccordionPanel>
        </AccordionItem>

        {/* Memory */}
        <AccordionItem value="memory">
          <AccordionHeader size="small">
            <div className={styles.accordionHeader}>
              <BrainCircuitRegular className={styles.sectionIcon} />
              <Text className={styles.sectionLabel}>Memory</Text>
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <Text className={styles.emptyState}>
              Memory and conversation history settings are managed in the Foundry Portal.
            </Text>
            <Button
              className={styles.editLink}
              appearance="subtle"
              size="small"
              icon={<OpenRegular />}
              as="a"
              href={foundryPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Manage memory in Foundry Portal
            </Button>
          </AccordionPanel>
        </AccordionItem>

        {/* Guardrails */}
        <AccordionItem value="guardrails">
          <AccordionHeader size="small">
            <div className={styles.accordionHeader}>
              <ShieldCheckmarkRegular className={styles.sectionIcon} />
              <Text className={styles.sectionLabel}>Guardrails</Text>
            </div>
          </AccordionHeader>
          <AccordionPanel>
            <Text className={styles.emptyState}>
              Content filters and safety guardrails are configured in the Foundry Portal.
            </Text>
            <Button
              className={styles.editLink}
              appearance="subtle"
              size="small"
              icon={<OpenRegular />}
              as="a"
              href={foundryPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Manage guardrails in Foundry Portal
            </Button>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

FoundryDetailsTab.displayName = 'FoundryDetailsTab';
